import { useState, useEffect, useMemo } from 'react';

/**
 * Mocked version of useOnboarding for testing/demo purposes
 * Mirrors the full API of useOnboarding but uses local state instead of real API calls
 */
export const useOnboardingMocked = () => {
  // Form inputs
  const [websiteUrlInput, setWebsiteUrlInput] = useState('https://www.example.com');

  // Flow state
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [flowState, setFlowState] = useState('not_started'); // 'not_started' | 'analyzing' | 'profile_ready' | 'generating_post' | 'completed'
  const [currentStep, setCurrentStep] = useState(0);
  const [profileError, setProfileError] = useState('');
  const [analysisProgress, setAnalysisProgress] = useState(0);

  // Summary & Offerings
  const [profileSummary, setProfileSummary] = useState(null);
  const [profileOfferings, setProfileOfferings] = useState(null);
  const [mockPosts, setMockPosts] = useState([]);

  // Mock data that gets populated when analysis completes
  const mockSummaryData = {
    user_id: 'mock-user-123',
    profile_id: 'mock-profile-456',
    business_name: 'Acme Coffee Roasters',
    logo: 'https://placehold.co/128x128/png',
    industry: 'Food & Beverage',
    country: 'USA',
    city: 'Portland',
    description:
      'We source and roast exceptional small-batch coffees with sustainable practices and a love for the craft. Our mission is to bring transparency and quality to every cup.',
    audience:
      'Coffee enthusiasts, local professionals, and remote workers seeking quality coffee and a welcoming space.',
    selling_points: [
      'Single-origin beans',
      'Sustainable sourcing',
      'Skilled baristas',
      'Direct trade partnerships',
    ],
    call_to_actions: [
      'Visit our cafe',
      'Shop online',
      'Subscribe for updates',
      'Join our loyalty program',
    ],
    core_values: ['Quality', 'Community', 'Sustainability', 'Craftsmanship'],
    voice_tone: 'Warm, friendly, knowledgeable, and passionate about coffee',
  };

  const mockOfferingsData = [
    {
      id: 'offering-1',
      name: 'House Espresso Blend',
      description:
        'Balanced and rich espresso blend with notes of chocolate and caramel. Perfect for milk-based drinks.',
      page_url: 'https://example.com/espresso',
    },
    {
      id: 'offering-2',
      name: 'Seasonal Single-Origin',
      description:
        'Rotating selection of limited single-origin coffees, lightly roasted to highlight terroir and origin characteristics.',
      page_url: 'https://example.com/single-origin',
    },
    {
      id: 'offering-3',
      name: 'Cold Brew Concentrate',
      description: 'Smooth, low-acidity cold brew concentrate for easy at-home coffee preparation.',
      page_url: 'https://example.com/cold-brew',
    },
  ];

  // Simulate analysis progress and state transitions
  useEffect(() => {
    if (flowState === 'analyzing') {
      const timer = setInterval(() => {
        setAnalysisProgress((p) => {
          const next = Math.min(p + Math.random() * 15, 100);
          if (next >= 100) {
            clearInterval(timer);
            // Transition to profile_ready after a small delay
            setTimeout(() => {
              setFlowState('profile_ready');
              setProfileSummary(mockSummaryData);
              setProfileOfferings(mockOfferingsData);
              setCurrentStep(1);
            }, 500);
            return 100;
          }
          return next;
        });
      }, 300);

      return () => clearInterval(timer);
    }
  }, [flowState]);

  // Simulate post generation
  useEffect(() => {
    if (flowState === 'generating_post') {
      const timer = setTimeout(() => {
        setMockPosts([
          {
            id: 'post-mock-feed-1',
            status: 'pending',
            title: 'Welcome to our cafe',
            content: 'Check out our amazing coffee selection!',
            scheduled_at: new Date().toISOString(),
            aspectRatio: '1:1',
            imageUrl: 'https://placehold.co/800x800/png',
            caption:
              'Discover the rich flavors of our expertly crafted coffees. #CoffeeLovers #AcmeCoffee',
          },
          {
            id: 'post-mock-story-1',
            status: 'pending',
            title: 'Today’s special story',
            content: 'Try our limited seasonal single-origin!',
            scheduled_at: new Date().toISOString(),
            aspectRatio: '9:16',
            imageUrl: 'https://placehold.co/720x1280/png',
            caption:
              'Experience the unique taste of our seasonal single-origin coffee. Available for a limited time! #SingleOrigin #AcmeCoffee',
          },
        ]);

        setFlowState('post_ready');
      }, 1200);

      return () => clearTimeout(timer);
    }
  }, [flowState]);

  const firstPendingStoryPost = useMemo(
    () => mockPosts.find((p) => p.status === 'pending' && p.aspectRatio === '9:16'),
    [mockPosts]
  );

  const firstPendingFeedPost = useMemo(
    () => mockPosts.find((p) => p.status === 'pending' && p.aspectRatio === '1:1'),
    [mockPosts]
  );

  const firstPendingPost = useMemo(
    () => (firstPendingStoryPost && firstPendingFeedPost) || null,
    [firstPendingStoryPost, firstPendingFeedPost]
  );

  // Handlers
  const handleWebsiteSubmit = (e) => {
    e?.preventDefault?.();
    if (websiteUrlInput.trim()) {
      setWebsiteUrl(websiteUrlInput);
      setFlowState('analyzing');
      setCurrentStep(1);
      setAnalysisProgress(5);
    }
  };

  const handleRetry = () => {
    setProfileError('');
    setAnalysisProgress(0);
    setFlowState('analyzing');
  };

  const handleStepChange = (stepIndex) => {
    setCurrentStep(stepIndex);

    // Start post generation when moving to step 2
    if (stepIndex === 2 && flowState === 'profile_ready') {
      setFlowState('generating_post');
    }
  };

  const handleFinish = () => {
    setFlowState('completed');
    // Could navigate or show success message here
  };

  // Summary handlers
  const handleSaveSummary = (updates) => {
    setProfileSummary((prev) => ({ ...prev, ...updates }));
  };

  // Offerings handlers
  const handleSaveOffering = (index, updated) => {
    setProfileOfferings((prev) => {
      const arr = [...prev];
      const i = typeof index === 'number' ? index : arr.findIndex((o) => o.id === updated.id);
      if (i >= 0) {
        arr[i] = { ...arr[i], ...updated };
      }
      return arr;
    });
  };

  const handleDeleteOffering = (id) => {
    setProfileOfferings((prev) => prev.filter((o) => o.id !== id));
  };

  const handleAddOffering = (data) => {
    const id = `offering-${Date.now()}`;
    setProfileOfferings((prev) => [...prev, { id, ...data }]);
  };

  // Mock mutation states (always idle/success for demo)
  const createProfileMutation = {
    isPending: flowState === 'analyzing',
    isSuccess:
      flowState === 'profile_ready' || flowState === 'generating_post' || flowState === 'completed',
    mutate: handleWebsiteSubmit,
  };

  const updateSummaryMutation = {
    isPending: false,
  };

  const updateOfferingMutation = {
    isPending: false,
  };

  return {
    // Inputs
    websiteUrlInput,
    setWebsiteUrlInput,

    // State
    websiteUrl,
    flowState,
    currentStep,
    profileError,
    analysisProgress,
    setAnalysisProgress,

    // Handlers
    handleWebsiteSubmit,
    handleRetry,
    handleStepChange,
    handleFinish,

    // Mutations
    createProfileMutation,

    // Summary & Offerings
    profileSummary,
    summaryLoading: false,
    summaryError: null,
    profileOfferings,
    offeringsLoading: false,
    offeringsError: null,
    handleSaveSummary,
    handleSaveOffering,
    handleDeleteOffering,
    handleAddOffering,
    updateSummaryMutation,
    updateOfferingMutation,

    // Posts
    firstPendingStoryPost,
    firstPendingFeedPost,
    firstPendingPost,
  };
};
